import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../store';
import { useEffect, useCallback, useState, useRef } from 'react';
import {
  LayoutDashboard, BookOpen, Brain, FileEdit, ClipboardCheck,
  AlertTriangle, Lightbulb, PenTool, Layers, Upload, Menu, X,
  ChevronDown, Star, MoreHorizontal, Grid3X3, Check
} from 'lucide-react';

const modules = [
  { id: 'dashboard', label: '学习仪表盘', shortLabel: '仪表盘', icon: LayoutDashboard },
  { id: 'materials', label: '资料分类', shortLabel: '资料', icon: Upload },
  { id: 'preview', label: '预习模块', shortLabel: '预习', icon: BookOpen },
  { id: 'exercises', label: '练习册', shortLabel: '练习', icon: FileEdit },
  { id: 'tests', label: '模拟测试', shortLabel: '测试', icon: ClipboardCheck },
  { id: 'mistakes', label: '错题本', shortLabel: '错题', icon: AlertTriangle },
  { id: 'difficulty', label: '难点解答', shortLabel: '难点', icon: Lightbulb },
  { id: 'essay', label: '论文编辑器', shortLabel: '论文', icon: PenTool },
  { id: 'flashcards', label: '闪读卡', shortLabel: '闪读卡', icon: Layers },
];

// Bottom nav shows 4 quick modules + "more" button
const bottomNavModules = [
  { id: 'dashboard', label: '仪表盘', icon: LayoutDashboard },
  { id: 'exercises', label: '练习', icon: FileEdit },
  { id: 'tests', label: '测试', icon: ClipboardCheck },
  { id: 'flashcards', label: '闪读卡', icon: Layers },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

// Mobile bottom sheet for "More" modules
function MoreSheet({ open, onClose, activeModule, onSelect }: {
  open: boolean;
  onClose: () => void;
  activeModule: string;
  onSelect: (id: string) => void;
}) {
  const otherModules = modules.filter(m => !bottomNavModules.some(bn => bn.id === m.id));

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[60] lg:hidden"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            className="fixed bottom-0 left-0 right-0 z-[61] lg:hidden"
          >
            <div className="bg-white rounded-t-3xl shadow-2xl border-t border-stone-100 overflow-hidden">
              {/* Handle bar */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-stone-200" />
              </div>

              {/* Title */}
              <div className="px-5 pb-2 pt-1">
                <h3 className="text-sm font-semibold text-stone-950 font-body">更多模块</h3>
              </div>

              {/* Module grid */}
              <div className="grid grid-cols-5 gap-1 px-4 pb-4">
                {otherModules.map(mod => {
                  const Icon = mod.icon;
                  const isActive = activeModule === mod.id;
                  return (
                    <button
                      key={mod.id}
                      onClick={() => {
                        onSelect(mod.id);
                        onClose();
                      }}
                      className={`flex flex-col items-center gap-1.5 py-3 px-1 rounded-2xl transition-all active:scale-95 ${
                        isActive ? 'bg-bronze/10' : 'hover:bg-stone-50 active:bg-stone-100'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                        isActive
                          ? 'bg-stone-950 text-white shadow-sm'
                          : 'bg-stone-100 text-stone-500'
                      }`}>
                        <Icon className="w-[18px] h-[18px]" />
                      </div>
                      <span className={`text-[10px] font-body leading-tight text-center ${
                        isActive ? 'text-stone-950 font-semibold' : 'text-stone-500'
                      }`}>
                        {mod.shortLabel}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Safe area spacer */}
              <div className="safe-bottom" />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Subject switcher dropdown for header
function SubjectSwitcher({ open, onClose, subjects, activeSubjectId, onSelect }: {
  open: boolean;
  onClose: () => void;
  subjects: { id: string; name: string; nameEn: string; level?: string; program: string; icon: string }[];
  activeSubjectId: string;
  onSelect: (id: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Delay listener to avoid immediate close from the same click
    const timer = setTimeout(() => document.addEventListener('click', handleClick), 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.96 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute top-full left-0 sm:left-auto mt-2 w-[calc(100vw-24px)] sm:w-[320px] bg-white rounded-2xl shadow-2xl border border-stone-100 overflow-hidden z-50"
        >
          <div className="px-4 pt-3.5 pb-2">
            <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-medium font-body">切换学科</p>
          </div>
          <div className="px-2 pb-2">
            {subjects.map(subject => {
              const isActive = subject.id === activeSubjectId;
              return (
                <button
                  key={subject.id}
                  onClick={() => {
                    onSelect(subject.id);
                    onClose();
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all active:scale-[0.98] touch-target ${
                    isActive
                      ? 'bg-stone-950/[0.04]'
                      : 'hover:bg-stone-50 active:bg-stone-100'
                  }`}
                >
                  <span className="text-xl flex-shrink-0 w-8 text-center">{subject.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] font-body truncate ${
                      isActive ? 'font-semibold text-stone-950' : 'text-stone-700'
                    }`}>
                      {subject.name}
                    </p>
                    <p className="text-[10px] text-stone-400 font-body">{subject.nameEn}</p>
                  </div>
                  {subject.level && (
                    <span className="text-[10px] px-2 py-0.5 rounded-lg bg-stone-100 text-stone-500 font-body flex-shrink-0">
                      {subject.level} · {subject.program}
                    </span>
                  )}
                  {isActive && (
                    <div className="w-5 h-5 rounded-full bg-stone-950 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function MainLayout({ children }: { children: React.ReactNode }) {
  const { subjects, activeSubjectId, setActiveSubject, activeModule, setActiveModule, sidebarOpen, toggleSidebar } = useAppStore();
  const { progress } = useAppStore();
  const activeSubject = subjects.find(s => s.id === activeSubjectId);
  const currentProgress = progress.find(p => p.subjectId === activeSubjectId);
  const isMobile = useIsMobile();
  const [moreOpen, setMoreOpen] = useState(false);
  const [subjectSwitcherOpen, setSubjectSwitcherOpen] = useState(false);

  // Close sidebar on mobile by default
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      toggleSidebar();
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeSidebarOnMobile = useCallback(() => {
    if (isMobile && sidebarOpen) {
      toggleSidebar();
    }
  }, [isMobile, sidebarOpen, toggleSidebar]);

  const handleModuleClick = (moduleId: string) => {
    setActiveModule(moduleId);
    closeSidebarOnMobile();
  };

  const handleSubjectClick = (subjectId: string) => {
    setActiveSubject(subjectId);
    closeSidebarOnMobile();
  };

  // Check if current module is in the "more" list
  const isInMoreList = !bottomNavModules.some(m => m.id === activeModule);

  return (
    <div className="flex h-screen overflow-hidden bg-linen">
      {/* Mobile overlay backdrop */}
      <AnimatePresence>
        {sidebarOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
            onClick={toggleSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="w-[280px] h-screen flex-shrink-0 bg-stone-950 text-white flex flex-col z-50 fixed lg:relative noise-overlay"
          >
            {/* Logo area */}
            <div className="relative z-10 p-5 border-b border-white/[0.06] safe-top">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl gradient-bronze flex items-center justify-center shadow-md">
                    <Brain className="w-5 h-5 text-stone-950" />
                  </div>
                  <div>
                    <h1 className="text-[15px] font-bold tracking-tight font-display">IB Scholar</h1>
                    <p className="text-[9px] text-white/35 tracking-[0.2em] uppercase font-body">Australia · MYP / DP</p>
                  </div>
                </div>
                <button onClick={toggleSidebar} className="p-2 rounded-lg hover:bg-white/[0.06] transition-colors active:bg-white/[0.1]">
                  <X className="w-4 h-4 text-white/50" />
                </button>
              </div>
            </div>

            {/* Subject selector */}
            <div className="relative z-10 p-4">
              <p className="text-[9px] uppercase tracking-[0.2em] text-white/25 mb-2.5 font-medium font-body">学科选择</p>
              <div className="space-y-0.5">
                {subjects.map(subject => (
                  <button
                    key={subject.id}
                    onClick={() => handleSubjectClick(subject.id)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[13px] transition-all font-body touch-target ${
                      activeSubjectId === subject.id
                        ? 'bg-white/[0.1] text-bronze-light shadow-inner-glow'
                        : 'text-white/45 hover:bg-white/[0.04] hover:text-white/75 active:bg-white/[0.08]'
                    }`}
                  >
                    <span className="text-base">{subject.icon}</span>
                    <span className="flex-1 text-left truncate">{subject.name}</span>
                    {subject.level && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-white/[0.06] text-white/40 flex-shrink-0">{subject.level}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Assessment criteria quick view */}
            {currentProgress && (
              <div className="relative z-10 px-4 pb-3">
                <p className="text-[9px] uppercase tracking-[0.2em] text-white/25 mb-2.5 font-medium font-body">评估标准概览</p>
                <div className="bg-white/[0.04] rounded-xl p-3.5 space-y-2.5 border border-white/[0.04]">
                  {currentProgress.assessmentScores.map(score => (
                    <div key={score.criterion} className="flex items-center gap-2.5">
                      <span className="text-[10px] font-bold text-bronze w-4 font-body">{score.criterion}</span>
                      <div className="flex-1 h-1 bg-white/[0.06] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${((score.currentScore || 0) / score.maxScore) * 100}%`,
                            background: 'linear-gradient(90deg, var(--sage-dark), var(--sage))'
                          }}
                        />
                      </div>
                      <span className="text-[10px] text-white/35 tabular-nums font-body">{score.currentScore}/{score.maxScore}</span>
                    </div>
                  ))}
                  <div className="pt-2 border-t border-white/[0.06] flex items-center justify-between">
                    <span className="text-[10px] text-white/30 font-body">预估等级</span>
                    <div className="flex items-center gap-1.5">
                      <Star className="w-3 h-3 text-bronze" />
                      <span className="text-sm font-bold text-bronze font-display">{currentProgress.predictedGrade}/7</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation modules */}
            <div className="relative z-10 flex-1 overflow-y-auto px-4 pb-4 overscroll-contain">
              <p className="text-[9px] uppercase tracking-[0.2em] text-white/25 mb-2.5 font-medium font-body">学习模块</p>
              <div className="space-y-0.5">
                {modules.map(mod => {
                  const Icon = mod.icon;
                  const isActive = activeModule === mod.id;
                  return (
                    <button
                      key={mod.id}
                      onClick={() => handleModuleClick(mod.id)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[13px] transition-all font-body group touch-target ${
                        isActive
                          ? 'bg-bronze/15 text-bronze-light font-medium'
                          : 'text-white/40 hover:bg-white/[0.04] hover:text-white/70 active:bg-white/[0.08]'
                      }`}
                    >
                      <Icon className={`w-[16px] h-[16px] flex-shrink-0 transition-colors ${isActive ? 'text-bronze' : 'group-hover:text-white/50'}`} />
                      <span>{mod.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Bottom info */}
            <div className="relative z-10 p-4 border-t border-white/[0.06] safe-bottom">
              <div className="flex items-center gap-2.5 text-white/25 text-[10px] font-body">
                <div className="w-1.5 h-1.5 rounded-full bg-sage animate-pulse" />
                <span className="truncate">IB DP · {activeSubject?.nameEn} · Term 1, 2026</span>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar — glassmorphism */}
        <header className="h-[56px] glass border-b border-stone-200/50 flex items-center px-3 sm:px-5 gap-2 sm:gap-4 flex-shrink-0 z-20 safe-top">
          {!sidebarOpen && (
            <button onClick={toggleSidebar} className="p-2.5 rounded-xl hover:bg-stone-950/[0.04] transition-colors active:bg-stone-950/[0.08] flex-shrink-0">
              <Menu className="w-5 h-5 text-stone-700" />
            </button>
          )}
          <div className="relative flex items-center gap-1 min-w-0 flex-1">
            <button
              onClick={() => setSubjectSwitcherOpen(!subjectSwitcherOpen)}
              className="flex items-center gap-2 min-w-0 px-2 py-1.5 -ml-2 rounded-xl hover:bg-stone-950/[0.04] active:bg-stone-950/[0.06] transition-colors"
            >
              <span className="text-lg flex-shrink-0">{activeSubject?.icon}</span>
              <h2 className="text-sm font-semibold text-stone-950 font-body truncate">{activeSubject?.name}</h2>
              <ChevronDown className={`w-3.5 h-3.5 text-stone-400 flex-shrink-0 transition-transform duration-200 ${subjectSwitcherOpen ? 'rotate-180' : ''}`} />
            </button>
            <span className="hidden sm:inline text-[11px] text-stone-400 bg-stone-100 px-2.5 py-0.5 rounded-lg font-body flex-shrink-0">{activeSubject?.level} · {activeSubject?.program}</span>

            {/* Subject switcher dropdown */}
            <SubjectSwitcher
              open={subjectSwitcherOpen}
              onClose={() => setSubjectSwitcherOpen(false)}
              subjects={subjects}
              activeSubjectId={activeSubjectId}
              onSelect={setActiveSubject}
            />
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <div className="text-right hidden sm:block">
              <p className="text-[10px] text-stone-400 font-body">今日学习进度</p>
              <p className="text-xs font-semibold text-stone-950 font-body">{currentProgress?.overallProgress || 0}% 完成</p>
            </div>
            <div className="w-9 h-9 rounded-xl gradient-warm flex items-center justify-center text-white text-[11px] font-bold font-display shadow-md flex-shrink-0">
              IB
            </div>
          </div>
        </header>

        {/* Page content — extra bottom padding on mobile for bottom nav */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 overscroll-contain">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeModule}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile bottom navigation bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 lg:hidden">
        <div className="glass-bottom-nav border-t border-stone-200/60">
          <div className="flex items-stretch">
            {bottomNavModules.map(mod => {
              const Icon = mod.icon;
              const isActive = activeModule === mod.id;
              return (
                <button
                  key={mod.id}
                  onClick={() => setActiveModule(mod.id)}
                  className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 pt-2.5 transition-all active:scale-95 relative ${
                    isActive ? 'text-stone-950' : 'text-stone-400'
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="bottomNavIndicator"
                      className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-full bg-stone-950"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <Icon className={`w-[20px] h-[20px] transition-colors ${isActive ? 'text-stone-950' : 'text-stone-400'}`} />
                  <span className={`text-[10px] font-body leading-tight ${isActive ? 'font-semibold text-stone-950' : 'text-stone-400'}`}>
                    {mod.label}
                  </span>
                </button>
              );
            })}
            {/* More button */}
            <button
              onClick={() => setMoreOpen(true)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 pt-2.5 transition-all active:scale-95 relative ${
                isInMoreList ? 'text-stone-950' : 'text-stone-400'
              }`}
            >
              {isInMoreList && (
                <motion.div
                  layoutId="bottomNavIndicator"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-full bg-stone-950"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <Grid3X3 className={`w-[20px] h-[20px] transition-colors ${isInMoreList ? 'text-stone-950' : 'text-stone-400'}`} />
              <span className={`text-[10px] font-body leading-tight ${isInMoreList ? 'font-semibold text-stone-950' : 'text-stone-400'}`}>
                更多
              </span>
            </button>
          </div>
          {/* Safe area spacer for iOS home indicator */}
          <div className="safe-bottom" />
        </div>
      </nav>

      {/* More modules sheet */}
      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        activeModule={activeModule}
        onSelect={setActiveModule}
      />
    </div>
  );
}
